import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiMutate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MoveCard from "@/components/MoveCard";
import MomentumRing from "@/components/MomentumRing";
import RecapCard from "@/components/RecapCard";
import PrepTomorrowCard from "@/components/PrepTomorrowCard";
import { Sparkles, RefreshCcw, ArrowRight, Compass } from "lucide-react";
import { toast } from "sonner";

/**
 * Today — PintaFlow's primary home experience.
 *
 * Core philosophy (baked into copy + layout):
 * "Complete these 3 moves today. Everything else can wait."
 *
 * Structure:
 *   1. Greeting + philosophy line
 *   2. 3 Move cards (the hero)
 *   3. Creator Momentum (feedback, secondary)
 *   4. "Explore more" — unlocked / softened after 3/3 done
 */
export default function Today() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenBusy, setRegenBusy] = useState(false);

  async function load() {
    try {
      const [d, r] = await Promise.all([
        apiGet("/today"),
        apiGet("/today/recap").catch(() => null),
      ]);
      setData(d);
      setRecap(r);
    } catch (e) {
      toast.error("Couldn't load today's moves — check your connection.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleMoveChange(updated) {
    setData((cur) => {
      if (!cur) return cur;
      const moves = cur.moves.map((m) => (m.id === updated.id ? updated : m));
      const done = moves.filter((m) => m.status === "done").length;
      return {
        ...cur,
        moves,
        progress: { done, total: moves.length, all_done: done === moves.length && moves.length > 0 },
      };
    });
  }

  async function regenerate() {
    setRegenBusy(true);
    try {
      const r = await apiMutate("POST", "/today/generate", {});
      setData((cur) => ({ ...cur, moves: r.moves, momentum: r.momentum, progress: { done: 0, total: r.moves.length, all_done: false } }));
      toast.success("Fresh moves loaded ✨");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't regenerate";
      toast.error(msg);
    } finally {
      setRegenBusy(false);
    }
  }

  if (loading) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center text-xs uppercase tracking-[0.3em]"
        style={{ color: "var(--ink-55)" }}
      >
        Reading your momentum…
      </div>
    );
  }

  if (!data) return null;

  const { greeting, philosophy, moves, momentum, progress } = data;
  const allDone = progress.all_done;

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="today-page">
      {/* HERO — greeting + philosophy */}
      <div className="mb-8 sm:mb-10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] mb-2" style={{ color: "var(--seal)" }}>
          <Sparkles size={12} strokeWidth={1.8} /> Today · {new Date(data.date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
        </div>
        <h1
          data-testid="today-greeting"
          className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3"
          style={{ color: "var(--ink)", fontVariationSettings: "'opsz' 48, 'wght' 500" }}
        >
          {greeting} <span aria-hidden>👋</span>
        </h1>
        <p
          data-testid="today-philosophy"
          className="text-base sm:text-lg leading-relaxed"
          style={{ color: "var(--ink-72)", maxWidth: "40rem" }}
        >
          {philosophy}
        </p>
      </div>

      {/* RECAP — yesterday + 7-day sparkline (hidden on day 1) */}
      <RecapCard recap={recap} />

      {/* MOVES — the hero */}
      <div className="mb-10">
        <div className="flex items-end justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--seal)" }}>
              Today&apos;s 3 Moves
            </div>
            <div
              data-testid="today-progress"
              className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
              style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink-72)" }}
            >
              {progress.done}/{progress.total} done
            </div>
          </div>
          {!allDone && (
            <button
              onClick={regenerate}
              disabled={regenBusy || progress.done > 0}
              data-testid="today-regenerate"
              className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1.5 rounded-full inline-flex items-center gap-1.5"
              style={{
                background: "transparent",
                border: "1px solid var(--ink-22)",
                color: progress.done > 0 ? "var(--ink-55)" : "var(--ink-72)",
                opacity: progress.done > 0 ? 0.5 : 1,
              }}
              title={progress.done > 0 ? "Can't regenerate after starting" : "Roll a new set"}
            >
              <RefreshCcw size={11} strokeWidth={1.8} /> New set
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {moves.map((m, i) => (
            <MoveCard key={m.id} move={m} index={i} onChange={handleMoveChange} />
          ))}
        </div>

        {/* All-done celebration */}
        {allDone && (
          <div
            data-testid="today-all-done"
            className="mt-6 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            style={{
              background: "rgba(216,168,87,0.08)",
              border: "1px solid rgba(216,168,87,0.45)",
            }}
          >
            <div className="text-3xl" aria-hidden>🌅</div>
            <div className="flex-1">
              <div className="font-heading text-lg sm:text-xl" style={{ color: "var(--ink)" }}>
                You did it. Today&apos;s growth is locked in.
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--ink-72)" }}>
                Everything below is bonus. Explore if you feel it, or come back tomorrow — a fresh set is waiting at sunrise.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MOMENTUM — feedback layer */}
      <div
        className="rounded-2xl p-5 sm:p-6 mb-8"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
        data-testid="momentum-panel"
      >
        <div className="flex items-start gap-5 mb-5">
          <MomentumRing overall={momentum.overall} label={momentum.label} emoji={momentum.emoji} />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: "var(--seal)" }}>
              Creator Momentum
            </div>
            <div className="font-heading text-xl sm:text-2xl leading-tight" style={{ color: "var(--ink)" }}>
              {momentum.emoji} {momentum.label}
            </div>
            <div className="text-sm mt-1.5" style={{ color: "var(--ink-72)" }}>
              {momentum.vibe}
            </div>
          </div>
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
          {Object.entries(momentum.dimensions).map(([key, d]) => (
            <DimensionCell key={key} dim={key} data={d} />
          ))}
        </div>

        <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-center" style={{ color: "var(--ink-55)" }}>
          Highlighted = Growth Opportunities · Every dimension moves with your daily reps.
        </div>
      </div>

      {/* Explore more (soft, only when relevant) */}
      <ExploreMore allDone={allDone} />

      {/* Prep Tomorrow — Option D */}
      <PrepTomorrowCard />
    </div>
  );
}

function DimensionCell({ dim, data }) {
  const highlight = data.is_growth_opportunity;
  return (
    <div
      data-testid={`momentum-dim-${dim}`}
      className="rounded-xl p-3 flex flex-col gap-1.5 transition-colors"
      style={{
        background: highlight ? "rgba(216,168,87,0.08)" : "var(--bg)",
        border: `1px solid ${highlight ? "rgba(216,168,87,0.35)" : "var(--ink-22)"}`,
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.20em]" style={{ color: highlight ? "var(--seal)" : "var(--ink-55)" }}>
          {data.label}
        </div>
        <div className="font-mono text-sm" style={{ color: "var(--ink)" }}>
          {data.score}
        </div>
      </div>
      {/* micro-bar */}
      <div
        className="h-1 rounded-full overflow-hidden"
        style={{ background: "var(--ink-22)" }}
      >
        <div
          className="h-full"
          style={{
            width: `${data.score}%`,
            background: highlight ? "var(--seal)" : "rgba(216,168,87,0.55)",
            transition: "width 700ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </div>
      <div className="text-[11px] leading-snug mt-0.5" style={{ color: "var(--ink-72)" }}>
        {highlight ? <span style={{ color: "var(--seal)" }}>Growth opportunity — </span> : null}
        {data.insight}
      </div>
    </div>
  );
}

function ExploreMore({ allDone }) {
  const items = [
    { to: "/pipeline", label: "Pipeline", desc: "Ideas in production" },
    { to: "/vault", label: "Vault", desc: "Your idea library" },
    { to: "/calendar", label: "Calendar", desc: "What's scheduled" },
    { to: "/scripts", label: "Scripts", desc: "AI script writer" },
    { to: "/analytics", label: "Analytics", desc: "Numbers & trends" },
    { to: "/insights", label: "Insights", desc: "Deep dashboard" },
  ];
  return (
    <div>
      <div
        className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.28em]"
        style={{ color: "var(--ink-55)" }}
      >
        <Compass size={12} strokeWidth={1.8} /> {allDone ? "Bonus — explore when ready" : "Everything else can wait"}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" style={{ opacity: allDone ? 1 : 0.68 }}>
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`explore-${it.to.slice(1)}`}
            className="rounded-xl p-3 flex items-center justify-between group transition-colors"
            style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
          >
            <div>
              <div className="text-sm" style={{ fontVariationSettings: "'wght' 500" }}>{it.label}</div>
              <div className="text-[11px]" style={{ color: "var(--ink-55)" }}>{it.desc}</div>
            </div>
            <ArrowRight size={14} className="opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: "var(--seal)" }} />
          </Link>
        ))}
      </div>
    </div>
  );
}
